import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  defaultDropAnimationSideEffects,
  type DropAnimation,
} from '@dnd-kit/core';
import { NO_SENSORS } from '../lib/no-sensors';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { useBoardPermission } from '@/hooks/use-boards';
import { CSS } from '@dnd-kit/utilities';
import { AssigneeStack } from './assignee-stack';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TaskItem, TaskStatusItem } from '@hygge/shared';
import { SprintStatusBadge } from './sprint-status-badge';
import {
  useActiveSprint,
  useBacklog,
  useSprints,
  useAddTasksToSprint,
  useCreateSprint,
  useRemoveTasksFromSprint,
} from '@/hooks/use-sprints';
import { useTasks } from '@/hooks/use-tasks';
import { ChevronsUpDown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/lib/errors';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PriorityBars, PRIORITY_CONFIG } from './task-config';
import { nextSprintName } from '../lib/sprint-name';

interface BacklogColumnsViewProps {
  projectId: string;
  statuses: TaskStatusItem[];
  sprintId: string | null;
  onTaskSelect?: (key: string) => void;
  onCreateTask?: () => void;
}

const dropAnimation: DropAnimation = {
  duration: 200,
  easing: 'ease',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

const SPRINT_DROPPABLE = 'sprint-column';
const BACKLOG_DROPPABLE = 'backlog-column';

export function BacklogColumnsView({
  projectId,
  statuses,
  sprintId,
  onTaskSelect,
  onCreateTask,
}: BacklogColumnsViewProps) {
  const { data: activeSprint } = useActiveSprint(projectId);
  const { data: allSprints } = useSprints(projectId);
  const { data: backlogTasks } = useBacklog(projectId);

  const nonCompletedSprints = useMemo(
    () => (allSprints ?? []).filter((s) => s.status !== 'completed' && s.status !== 'cancelled'),
    [allSprints],
  );

  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);

  const defaultSprint = activeSprint || nonCompletedSprints[0] || null;
  const currentSprint = useMemo(() => {
    const id = selectedSprintId ?? sprintId;
    if (!id) return defaultSprint;
    return nonCompletedSprints.find((s) => s.id === id) || defaultSprint;
  }, [selectedSprintId, sprintId, nonCompletedSprints, defaultSprint]);

  const { data: sprintTasksData } = useTasks(
    currentSprint ? projectId : undefined,
    currentSprint ? { sprintId: currentSprint.id } : undefined,
  );
  const serverSprintTasks = sprintTasksData?.data ?? [];
  const serverBacklogTasks = backlogTasks ?? [];

  // Optimistic state for cross-column DnD moves
  const [optimisticMove, setOptimisticMove] = useState<{
    taskId: string;
    direction: 'toSprint' | 'toBacklog';
  } | null>(null);

  const sprintTasks = useMemo(() => {
    if (!optimisticMove) return serverSprintTasks;
    if (optimisticMove.direction === 'toSprint') {
      const movedTask = serverBacklogTasks.find((t) => t.id === optimisticMove.taskId);
      if (movedTask) return [...serverSprintTasks, movedTask];
    }
    if (optimisticMove.direction === 'toBacklog') {
      return serverSprintTasks.filter((t) => t.id !== optimisticMove.taskId);
    }
    return serverSprintTasks;
  }, [serverSprintTasks, serverBacklogTasks, optimisticMove]);

  const effectiveBacklogTasks = useMemo(() => {
    if (!optimisticMove) return serverBacklogTasks;
    if (optimisticMove.direction === 'toBacklog') {
      const movedTask = serverSprintTasks.find((t) => t.id === optimisticMove.taskId);
      if (movedTask) return [...serverBacklogTasks, movedTask];
    }
    if (optimisticMove.direction === 'toSprint') {
      return serverBacklogTasks.filter((t) => t.id !== optimisticMove.taskId);
    }
    return serverBacklogTasks;
  }, [serverSprintTasks, serverBacklogTasks, optimisticMove]);

  // Clear optimistic state when server data updates
  const serverSprintLen = serverSprintTasks.length;
  const serverBacklogLen = serverBacklogTasks.length;
  useEffect(() => {
    setOptimisticMove(null);
  }, [serverSprintLen, serverBacklogLen]);

  // `projectId` is the board id here — see how tasks-page renders this view.
  const canManageSprints = useBoardPermission(projectId, 'sprint.manage');
  // Every drag here lands on a `task.manage` route (sprint add/remove, reorder).
  const canMove = useBoardPermission(projectId, 'task.manage');
  const createSprint = useCreateSprint(projectId);
  const [activeId, setActiveId] = useState<string | null>(null);

  /**
   * Planning a sprint is what this screen is for, and until now the only place
   * that could make one was the Overview tab — the lifecycle actions moved
   * there and left the backlog with nothing to plan into. It sits in the sprint
   * switcher because that is where someone goes when the sprint they want is
   * not the one on screen.
   */
  const handleCreateSprint = useCallback(() => {
    createSprint.mutate(
      { name: nextSprintName((allSprints ?? []).map((s) => s.name)) },
      {
        // Select it at once: a new sprint you cannot see is indistinguishable
        // from a button that did nothing.
        onSuccess: (created) => {
          setSelectedSprintId(created.id);
          toast.success(`${created.name} created`);
        },
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to create sprint')),
      },
    );
  }, [createSprint, allSprints]);

  const addToSprint = useAddTasksToSprint(projectId);
  const removeFromSprint = useRemoveTasksFromSprint(projectId);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  // No sensor at all when the board does not allow moving issues — see board-view.
  const allSensors = useSensors(pointerSensor);
  const sensors = canMove ? allSensors : NO_SENSORS;

  const allTasks = useMemo(() => {
    const map = new Map<string, TaskItem>();
    for (const t of sprintTasks) map.set(t.id, t);
    for (const t of effectiveBacklogTasks) map.set(t.id, t);
    return map;
  }, [sprintTasks, effectiveBacklogTasks]);

  const activeTask = activeId ? allTasks.get(activeId) ?? null : null;

  const statusMap = useMemo(() => {
    const m = new Map<string, TaskStatusItem>();
    for (const s of statuses) m.set(s.id, s);
    return m;
  }, [statuses]);

  const sprintTaskIds = useMemo(() => sprintTasks.map((t) => t.id), [sprintTasks]);
  const backlogTaskIds = useMemo(() => effectiveBacklogTasks.map((t) => t.id), [effectiveBacklogTasks]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !currentSprint) return;

      const taskId = active.id as string;
      const overId = over.id as string;
      const isInSprint = sprintTaskIds.includes(taskId);

      let targetColumn: string | null = null;
      if (overId === SPRINT_DROPPABLE || sprintTaskIds.includes(overId)) {
        targetColumn = SPRINT_DROPPABLE;
      } else if (overId === BACKLOG_DROPPABLE || backlogTaskIds.includes(overId)) {
        targetColumn = BACKLOG_DROPPABLE;
      }

      if (targetColumn === SPRINT_DROPPABLE && !isInSprint) {
        setOptimisticMove({ taskId, direction: 'toSprint' });
        addToSprint.mutate({ sprintId: currentSprint.id, taskIds: [taskId] });
      } else if (targetColumn === BACKLOG_DROPPABLE && isInSprint) {
        setOptimisticMove({ taskId, direction: 'toBacklog' });
        removeFromSprint.mutate({ sprintId: currentSprint.id, taskIds: [taskId] });
      }
    },
    [currentSprint, sprintTaskIds, backlogTaskIds, addToSprint, removeFromSprint],
  );

  const sprintPoints = sprintTasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);
  const backlogPoints = effectiveBacklogTasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);

  return (
    <TooltipProvider delayDuration={300}>
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full flex gap-4 p-4 overflow-hidden">
        {/* Backlog Column */}
        <DroppableColumn
          id={BACKLOG_DROPPABLE}
          header={
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 shrink-0">
              <span className="text-sm font-semibold text-foreground">Backlog</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {effectiveBacklogTasks.length} task{effectiveBacklogTasks.length !== 1 ? 's' : ''}
              </span>
              {backlogPoints > 0 && (
                <span className="text-xs text-muted-foreground/60 font-mono tabular-nums">
                  · {backlogPoints} pts
                </span>
              )}
            </div>
          }
          taskIds={backlogTaskIds}
        >
          {effectiveBacklogTasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-1.5">
              <p className="text-xs text-muted-foreground/60 text-center">
                All tasks are assigned to a sprint.
                {onCreateTask && (
                  <>
                    {' '}You can{' '}
                    <button
                      type="button"
                      className="font-medium text-hygge hover:text-hygge/80 transition-colors cursor-pointer"
                      onClick={onCreateTask}
                    >
                      create new
                    </button>
                    .
                  </>
                )}
              </p>
            </div>
          ) : (
            effectiveBacklogTasks.map((task) => (
              <TaskTile
                key={task.id}
                task={task}
                status={statusMap.get(task.statusId)}
                onSelect={onTaskSelect}
                canDrag={canMove}
              />
            ))
          )}
        </DroppableColumn>

        {/* Sprint Column */}
        <DroppableColumn
          id={SPRINT_DROPPABLE}
          header={
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/50 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                {/* Sprint switcher */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-hygge cursor-pointer transition-colors truncate"
                    >
                      <span className="truncate">{currentSprint?.name ?? 'No sprint'}</span>
                      <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {nonCompletedSprints.map((s) => (
                      <DropdownMenuItem
                        key={s.id}
                        onClick={() => setSelectedSprintId(s.id)}
                        className={cn('cursor-pointer', s.id === currentSprint?.id && 'bg-accent')}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="truncate flex-1">{s.name}</span>
                          <SprintStatusBadge status={s.status as 'planning' | 'active'} className="shrink-0" />
                          <span className="text-[10px] text-muted-foreground tabular-nums">{s.taskCount}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {canManageSprints && (
                      <>
                        {nonCompletedSprints.length > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuItem
                          onClick={handleCreateSprint}
                          disabled={createSprint.isPending}
                          className="cursor-pointer"
                        >
                          <Plus className="mr-2 h-3.5 w-3.5" />
                          {createSprint.isPending ? 'Creating…' : 'New sprint'}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {currentSprint && (
                  <SprintStatusBadge
                    status={currentSprint.status as 'planning' | 'active'}
                    className="shrink-0"
                  />
                )}
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {sprintTasks.length} task{sprintTasks.length !== 1 ? 's' : ''}
                </span>
                {sprintPoints > 0 && (
                  <span className="text-xs text-muted-foreground/60 font-mono tabular-nums shrink-0">
                    · {sprintPoints} pts
                  </span>
                )}
              </div>
            </div>
          }
          taskIds={sprintTaskIds}
        >
          {sprintTasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-1.5">
              <p className="text-xs text-muted-foreground/60 text-center">
                {/* Only offered to a role that may move issues; the drag is not there otherwise. */}
                {canMove ? 'Drag tasks here to plan your sprint.' : 'No tasks in this sprint yet.'}
                {onCreateTask && (
                  <>
                    {' '}Or{' '}
                    <button
                      type="button"
                      className="font-medium text-hygge hover:text-hygge/80 transition-colors cursor-pointer"
                      onClick={onCreateTask}
                    >
                      create new
                    </button>
                    .
                  </>
                )}
              </p>
            </div>
          ) : (
            sprintTasks.map((task) => (
              <TaskTile
                key={task.id}
                task={task}
                status={statusMap.get(task.statusId)}
                onSelect={onTaskSelect}
                canDrag={canMove}
              />
            ))
          )}
        </DroppableColumn>
      </div>

      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeTask ? (
            <TaskTileOverlay task={activeTask} status={statusMap.get(activeTask.statusId)} />
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
    </TooltipProvider>
  );
}

// ─── Droppable Column ──────────────────────────────────────────────────

function DroppableColumn({
  id,
  header,
  taskIds,
  children,
}: {
  id: string;
  header: React.ReactNode;
  taskIds: string[];
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 min-w-0 flex flex-col rounded-xl border border-border bg-muted/20 transition-colors overflow-hidden',
        isOver && 'ring-2 ring-primary/20 border-primary/30 bg-primary/[0.02]',
      )}
    >
      {header}

      {/* Scrollable task area */}
      <SortableContext items={taskIds} strategy={rectSortingStrategy}>
        <div className="flex-1 overflow-y-auto p-2 flex flex-col">
          <div className={cn(
            taskIds.length > 0
              ? 'grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-1.5'
              : 'flex-1 flex',
          )}>
            {children}
          </div>
        </div>
      </SortableContext>
    </div>
  );
}

// ─── Sprint Status Badge ────────────────────────────────────────────────

// ─── Task Tile ──────────────────────────────────────────────────────────

const TaskTile = memo(function TaskTile({
  task,
  status,
  onSelect,
  canDrag = true,
}: {
  task: TaskItem;
  status?: TaskStatusItem;
  onSelect?: (key: string) => void;
  /** False when the board does not allow moving issues: a plain, clickable tile. */
  canDrag?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canDrag });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
  };

  const priorityCfg = PRIORITY_CONFIG[task.priority];
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group rounded-md border border-border/60 bg-card p-2',
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        'hover:border-border hover:shadow-sm transition-all',
        isDragging && 'shadow-lg ring-2 ring-primary/20',
      )}
      onClick={() => onSelect?.(task.key)}
    >
      {/* Key + priority row */}
      <div className="flex items-center gap-1 mb-1">
        <div
          className="h-1.5 w-1.5 rounded-full shrink-0"
          style={{ backgroundColor: status?.color }}
        />
        <span className="text-[10px] font-mono font-semibold text-hygge truncate">
          {task.key}
        </span>
        <span className="flex-1" />
        {task.storyPoints != null && task.storyPoints > 0 && (
          <span className="text-[9px] font-mono text-muted-foreground tabular-nums bg-muted rounded px-1 py-px">
            {task.storyPoints}
          </span>
        )}
        <PriorityBars bars={priorityCfg.bars} className={cn('h-2.5 w-2.5', priorityCfg.color)} />
      </div>

      {/* Title */}
      <p className="text-[11px] text-foreground leading-tight line-clamp-2 min-h-[2lh]">
        {task.title}
      </p>

      {/* Assignee */}
      {(() => {
        const people = task.assignees ?? (task.assignee ? [task.assignee] : []);
        if (people.length === 0) return null;
        return (
          <div className="flex items-center gap-1 mt-1.5">
            <AssigneeStack people={people} size="xs" />
            <span className="text-[10px] text-muted-foreground truncate">
              {people.length === 1 ? people[0].firstName : `${people.length} assignees`}
            </span>
          </div>
        );
      })()}
    </div>
  );
});

// ─── Drag Overlay Tile ──────────────────────────────────────────────────

function TaskTileOverlay({ task, status }: { task: TaskItem; status?: TaskStatusItem }) {
  const priorityCfg = PRIORITY_CONFIG[task.priority];
  return (
    <div className="rounded-md border border-border bg-card p-2 shadow-2xl ring-2 ring-primary/20 w-[200px]">
      <div className="flex items-center gap-1 mb-1">
        <div
          className="h-1.5 w-1.5 rounded-full shrink-0"
          style={{ backgroundColor: status?.color }}
        />
        <span className="text-[10px] font-mono font-semibold text-hygge truncate">
          {task.key}
        </span>
        <span className="flex-1" />
        {task.storyPoints != null && task.storyPoints > 0 && (
          <span className="text-[9px] font-mono text-muted-foreground tabular-nums bg-muted rounded px-1 py-px">
            {task.storyPoints}
          </span>
        )}
        <PriorityBars bars={priorityCfg.bars} className={cn('h-2.5 w-2.5', priorityCfg.color)} />
      </div>
      <p className="text-[11px] text-foreground leading-tight line-clamp-2">
        {task.title}
      </p>
    </div>
  );
}
